import { useState, useEffect } from 'react';
import { Save, Users, BookOpen, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api, { classApi, studentApi, teacherApi } from '../lib/api';

// Jours de la semaine (Lundi à Vendredi)
const WEEKDAYS = [
  { id: 1, name_fr: 'Lundi', name_ar: 'الإثنين' },
  { id: 2, name_fr: 'Mardi', name_ar: 'الثلاثاء' },
  { id: 3, name_fr: 'Mercredi', name_ar: 'الأربعاء' },
  { id: 4, name_fr: 'Jeudi', name_ar: 'الخميس' },
  { id: 5, name_fr: 'Vendredi', name_ar: 'الجمعة' },
];

// Statuts d'évaluation - Valeurs backend: suivi, non_suivi, absent, malade
const EVAL_STATUSES = [
  { value: 'suivi', label: 'حـ', color: 'bg-green-500 hover:bg-green-600', title: 'Suivi' },
  { value: 'non_suivi', label: 'لم', color: 'bg-red-500 hover:bg-red-600', title: 'Non suivi' },
  { value: 'absent', label: 'غ', color: 'bg-gray-500 hover:bg-gray-600', title: 'Absent' },
  { value: 'malade', label: 'م', color: 'bg-yellow-500 hover:bg-yellow-600', title: 'Malade' },
];

// Types d'évaluation
const EVAL_TYPES = [
  { value: 'bind', label: 'بند', label_fr: 'Bind' },
  { value: 'wett', label: 'وط', label_fr: 'Wett' },
  { value: 'nafarr', label: 'نفر', label_fr: 'Nafarr' },
];

// Fonction pour obtenir les dates de la semaine
const getWeekDates = (weekOffset = 0) => {
  const now = new Date();
  const currentDay = now.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
  
  const dates = [];
  for (let i = 0; i < 5; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  return dates;
};

// Fonction pour obtenir le numéro de semaine
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

export default function QuranicDailyEvalPage() {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const [activeTab, setActiveTab] = useState('bind');
  const [evaluations, setEvaluations] = useState({});
  const [existingEvals, setExistingEvals] = useState([]);
  const [studentSurahData, setStudentSurahData] = useState({});
  const [studentProgress, setStudentProgress] = useState({}); // Progression des élèves

  // Liste des 114 Sourates du Coran
  const SURAHS = [
    { id: 1, name: 'الفاتحة', name_fr: 'Al-Fatiha', verses: 7 },
    { id: 2, name: 'البقرة', name_fr: 'Al-Baqara', verses: 286 },
    { id: 3, name: 'آل عمران', name_fr: 'Al-Imran', verses: 200 },
    { id: 4, name: 'النساء', name_fr: 'An-Nisa', verses: 176 },
    { id: 5, name: 'المائدة', name_fr: 'Al-Maida', verses: 120 },
    { id: 6, name: 'الأنعام', name_fr: 'Al-Anam', verses: 165 },
    { id: 7, name: 'الأعراف', name_fr: 'Al-Araf', verses: 206 },
    { id: 8, name: 'الأنفال', name_fr: 'Al-Anfal', verses: 75 },
    { id: 9, name: 'التوبة', name_fr: 'At-Tawba', verses: 129 },
    { id: 10, name: 'يونس', name_fr: 'Yunus', verses: 109 },
    { id: 11, name: 'هود', name_fr: 'Hud', verses: 123 },
    { id: 12, name: 'يوسف', name_fr: 'Yusuf', verses: 111 },
    { id: 13, name: 'الرعد', name_fr: 'Ar-Rad', verses: 43 },
    { id: 14, name: 'إبراهيم', name_fr: 'Ibrahim', verses: 52 },
    { id: 15, name: 'الحجر', name_fr: 'Al-Hijr', verses: 99 },
    { id: 16, name: 'النحل', name_fr: 'An-Nahl', verses: 128 },
    { id: 17, name: 'الإسراء', name_fr: 'Al-Isra', verses: 111 },
    { id: 18, name: 'الكهف', name_fr: 'Al-Kahf', verses: 110 },
    { id: 19, name: 'مريم', name_fr: 'Maryam', verses: 98 },
    { id: 20, name: 'طه', name_fr: 'Ta-Ha', verses: 135 },
    { id: 21, name: 'الأنبياء', name_fr: 'Al-Anbiya', verses: 112 },
    { id: 22, name: 'الحج', name_fr: 'Al-Hajj', verses: 78 },
    { id: 23, name: 'المؤمنون', name_fr: 'Al-Muminun', verses: 118 },
    { id: 24, name: 'النور', name_fr: 'An-Nur', verses: 64 },
    { id: 25, name: 'الفرقان', name_fr: 'Al-Furqan', verses: 77 },
    { id: 26, name: 'الشعراء', name_fr: 'Ash-Shuara', verses: 227 },
    { id: 27, name: 'النمل', name_fr: 'An-Naml', verses: 93 },
    { id: 28, name: 'القصص', name_fr: 'Al-Qasas', verses: 88 },
    { id: 29, name: 'العنكبوت', name_fr: 'Al-Ankabut', verses: 69 },
    { id: 30, name: 'الروم', name_fr: 'Ar-Rum', verses: 60 },
    { id: 31, name: 'لقمان', name_fr: 'Luqman', verses: 34 },
    { id: 32, name: 'السجدة', name_fr: 'As-Sajda', verses: 30 },
    { id: 33, name: 'الأحزاب', name_fr: 'Al-Ahzab', verses: 73 },
    { id: 34, name: 'سبأ', name_fr: 'Saba', verses: 54 },
    { id: 35, name: 'فاطر', name_fr: 'Fatir', verses: 45 },
    { id: 36, name: 'يس', name_fr: 'Ya-Sin', verses: 83 },
    { id: 37, name: 'الصافات', name_fr: 'As-Saffat', verses: 182 },
    { id: 38, name: 'ص', name_fr: 'Sad', verses: 88 },
    { id: 39, name: 'الزمر', name_fr: 'Az-Zumar', verses: 75 },
    { id: 40, name: 'غافر', name_fr: 'Ghafir', verses: 85 },
    { id: 41, name: 'فصلت', name_fr: 'Fussilat', verses: 54 },
    { id: 42, name: 'الشورى', name_fr: 'Ash-Shura', verses: 53 },
    { id: 43, name: 'الزخرف', name_fr: 'Az-Zukhruf', verses: 89 },
    { id: 44, name: 'الدخان', name_fr: 'Ad-Dukhan', verses: 59 },
    { id: 45, name: 'الجاثية', name_fr: 'Al-Jathiya', verses: 37 },
    { id: 46, name: 'الأحقاف', name_fr: 'Al-Ahqaf', verses: 35 },
    { id: 47, name: 'محمد', name_fr: 'Muhammad', verses: 38 },
    { id: 48, name: 'الفتح', name_fr: 'Al-Fath', verses: 29 },
    { id: 49, name: 'الحجرات', name_fr: 'Al-Hujurat', verses: 18 },
    { id: 50, name: 'ق', name_fr: 'Qaf', verses: 45 },
    { id: 51, name: 'الذاريات', name_fr: 'Adh-Dhariyat', verses: 60 },
    { id: 52, name: 'الطور', name_fr: 'At-Tur', verses: 49 },
    { id: 53, name: 'النجم', name_fr: 'An-Najm', verses: 62 },
    { id: 54, name: 'القمر', name_fr: 'Al-Qamar', verses: 55 },
    { id: 55, name: 'الرحمن', name_fr: 'Ar-Rahman', verses: 78 },
    { id: 56, name: 'الواقعة', name_fr: 'Al-Waqia', verses: 96 },
    { id: 57, name: 'الحديد', name_fr: 'Al-Hadid', verses: 29 },
    { id: 58, name: 'المجادلة', name_fr: 'Al-Mujadila', verses: 22 },
    { id: 59, name: 'الحشر', name_fr: 'Al-Hashr', verses: 24 },
    { id: 60, name: 'الممتحنة', name_fr: 'Al-Mumtahina', verses: 13 },
    { id: 61, name: 'الصف', name_fr: 'As-Saff', verses: 14 },
    { id: 62, name: 'الجمعة', name_fr: 'Al-Jumua', verses: 11 },
    { id: 63, name: 'المنافقون', name_fr: 'Al-Munafiqun', verses: 11 },
    { id: 64, name: 'التغابن', name_fr: 'At-Taghabun', verses: 18 },
    { id: 65, name: 'الطلاق', name_fr: 'At-Talaq', verses: 12 },
    { id: 66, name: 'التحريم', name_fr: 'At-Tahrim', verses: 12 },
    { id: 67, name: 'الملك', name_fr: 'Al-Mulk', verses: 30 },
    { id: 68, name: 'القلم', name_fr: 'Al-Qalam', verses: 52 },
    { id: 69, name: 'الحاقة', name_fr: 'Al-Haqqa', verses: 52 },
    { id: 70, name: 'المعارج', name_fr: 'Al-Maarij', verses: 44 },
    { id: 71, name: 'نوح', name_fr: 'Nuh', verses: 28 },
    { id: 72, name: 'الجن', name_fr: 'Al-Jinn', verses: 28 },
    { id: 73, name: 'المزمل', name_fr: 'Al-Muzzammil', verses: 20 },
    { id: 74, name: 'المدثر', name_fr: 'Al-Muddaththir', verses: 56 },
    { id: 75, name: 'القيامة', name_fr: 'Al-Qiyama', verses: 40 },
    { id: 76, name: 'الإنسان', name_fr: 'Al-Insan', verses: 31 },
    { id: 77, name: 'المرسلات', name_fr: 'Al-Mursalat', verses: 50 },
    { id: 78, name: 'النبأ', name_fr: 'An-Naba', verses: 40 },
    { id: 79, name: 'النازعات', name_fr: 'An-Naziat', verses: 46 },
    { id: 80, name: 'عبس', name_fr: 'Abasa', verses: 42 },
    { id: 81, name: 'التكوير', name_fr: 'At-Takwir', verses: 29 },
    { id: 82, name: 'الانفطار', name_fr: 'Al-Infitar', verses: 19 },
    { id: 83, name: 'المطففين', name_fr: 'Al-Mutaffifin', verses: 36 },
    { id: 84, name: 'الانشقاق', name_fr: 'Al-Inshiqaq', verses: 25 },
    { id: 85, name: 'البروج', name_fr: 'Al-Buruj', verses: 22 },
    { id: 86, name: 'الطارق', name_fr: 'At-Tariq', verses: 17 },
    { id: 87, name: 'الأعلى', name_fr: 'Al-Ala', verses: 19 },
    { id: 88, name: 'الغاشية', name_fr: 'Al-Ghashiya', verses: 26 },
    { id: 89, name: 'الفجر', name_fr: 'Al-Fajr', verses: 30 },
    { id: 90, name: 'البلد', name_fr: 'Al-Balad', verses: 20 },
    { id: 91, name: 'الشمس', name_fr: 'Ash-Shams', verses: 15 },
    { id: 92, name: 'الليل', name_fr: 'Al-Layl', verses: 21 },
    { id: 93, name: 'الضحى', name_fr: 'Ad-Duha', verses: 11 },
    { id: 94, name: 'الشرح', name_fr: 'Ash-Sharh', verses: 8 },
    { id: 95, name: 'التين', name_fr: 'At-Tin', verses: 8 },
    { id: 96, name: 'العلق', name_fr: 'Al-Alaq', verses: 19 },
    { id: 97, name: 'القدر', name_fr: 'Al-Qadr', verses: 5 },
    { id: 98, name: 'البينة', name_fr: 'Al-Bayyina', verses: 8 },
    { id: 99, name: 'الزلزلة', name_fr: 'Az-Zalzala', verses: 8 },
    { id: 100, name: 'العاديات', name_fr: 'Al-Adiyat', verses: 11 },
    { id: 101, name: 'القارعة', name_fr: 'Al-Qaria', verses: 11 },
    { id: 102, name: 'التكاثر', name_fr: 'At-Takathur', verses: 8 },
    { id: 103, name: 'العصر', name_fr: 'Al-Asr', verses: 3 },
    { id: 104, name: 'الهمزة', name_fr: 'Al-Humaza', verses: 9 },
    { id: 105, name: 'الفيل', name_fr: 'Al-Fil', verses: 5 },
    { id: 106, name: 'قريش', name_fr: 'Quraysh', verses: 4 },
    { id: 107, name: 'الماعون', name_fr: 'Al-Maun', verses: 7 },
    { id: 108, name: 'الكوثر', name_fr: 'Al-Kawthar', verses: 3 },
    { id: 109, name: 'الكافرون', name_fr: 'Al-Kafirun', verses: 6 },
    { id: 110, name: 'النصر', name_fr: 'An-Nasr', verses: 3 },
    { id: 111, name: 'المسد', name_fr: 'Al-Masad', verses: 5 },
    { id: 112, name: 'الإخلاص', name_fr: 'Al-Ikhlas', verses: 4 },
    { id: 113, name: 'الفلق', name_fr: 'Al-Falaq', verses: 5 },
    { id: 114, name: 'الناس', name_fr: 'An-Nas', verses: 6 },
  ];

  const weekDates = getWeekDates(weekOffset);
  const weekNumber = getWeekNumber(weekDates[0]);
  const weekYear = new Date(weekDates[0]).getFullYear();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedClass && weekDates.length > 0) {
      fetchExistingEvals();
    }
  }, [selectedClass, weekOffset]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [classesRes, studentsRes, teachersRes] = await Promise.all([
        classApi.getAll({}),
        studentApi.getAll({ is_active: true }),
        teacherApi.getAll({ is_active: true }),
      ]);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
      setTeachers(teachersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingEvals = async () => {
    try {
      // Récupérer les évaluations pour toute la semaine
      const promises = weekDates.map(date => 
        api.get(`/quranic-daily-evals?class_id=${selectedClass}&evaluation_date=${date}`)
      );
      const responses = await Promise.all(promises);
      
      const allEvals = responses.flatMap(r => r.data);
      setExistingEvals(allEvals);
      
      // Pré-remplir les évaluations existantes
      const evalsMap = {};
      const progressMap = {};
      
      // Trier par date pour obtenir la dernière évaluation
      const sortedEvals = [...allEvals].sort((a, b) => new Date(b.evaluation_date) - new Date(a.evaluation_date));
      
      sortedEvals.forEach(item => {
        const key = `${item.student_id}-${item.eval_type}-${item.evaluation_date}`;
        evalsMap[key] = item;
        
        // Stocker la dernière progression pour chaque élève (type bind uniquement)
        if (item.eval_type === 'bind' && item.surah_id && !progressMap[item.student_id]) {
          progressMap[item.student_id] = {
            lastSurah: item.surah_id,
            lastEndVerse: item.end_verse || 1,
            lastDate: item.evaluation_date
          };
        }
      });
      
      setEvaluations(evalsMap);
      setStudentProgress(progressMap);
      
      // Pré-remplir les champs avec la progression (continuer où l'élève s'est arrêté)
      const prefillData = {};
      Object.entries(progressMap).forEach(([studentId, prog]) => {
        const surah = SURAHS.find(s => s.id === prog.lastSurah);
        const nextVerse = (prog.lastEndVerse || 0) + 1;
        
        // Si on a fini la sourate, passer à la suivante
        if (surah && nextVerse > surah.verses) {
          const nextSurah = SURAHS.find(s => s.id === prog.lastSurah + 1);
          if (nextSurah) {
            prefillData[studentId] = {
              surah: String(nextSurah.id),
              startVerse: '1',
              endVerse: ''
            };
          }
        } else {
          prefillData[studentId] = {
            surah: String(prog.lastSurah),
            startVerse: String(nextVerse),
            endVerse: ''
          };
        }
      });
      setStudentSurahData(prefillData);
      
    } catch (error) {
      console.error('Erreur chargement évaluations:', error);
    }
  };

  const getClassStudents = () => {
    return students.filter(s => s.class_id === selectedClass);
  };

  const getClassName = () => {
    const cls = classes.find(c => c.id === selectedClass);
    return cls?.name || '';
  };

  const getTeacherForClass = () => {
    const cls = classes.find(c => c.id === selectedClass);
    if (cls?.teacher_id) {
      const teacher = teachers.find(t => t.id === cls.teacher_id);
      return teacher ? `${teacher.first_name} ${teacher.last_name}` : '';
    }
    return '';
  };

  const updateEvaluation = (studentId, evalType, date, status) => {
    const key = `${studentId}-${evalType}-${date}`;
    const surahData = studentSurahData[studentId] || {};
    setEvaluations(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        student_id: studentId,
        class_id: selectedClass,
        evaluation_date: date,
        eval_type: evalType,
        status: status,
        surah_id: surahData.surah || null,
        start_verse: surahData.startVerse || null,
        end_verse: surahData.endVerse || null,
      }
    }));
  };

  const updateStudentSurah = (studentId, field, value) => {
    setStudentSurahData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const getEvaluation = (studentId, evalType, date) => {
    const key = `${studentId}-${evalType}-${date}`;
    return evaluations[key] || {};
  };

  const handleSaveAll = async () => {
    const evalsToSave = Object.values(evaluations).filter(e => e.status);
    
    if (evalsToSave.length === 0) {
      toast.error('Aucune évaluation à enregistrer');
      return;
    }

    setSaving(true);
    try {
      await api.post('/quranic-daily-evals/batch', { evaluations: evalsToSave });
      toast.success(`${evalsToSave.length} évaluations enregistrées`);
      fetchExistingEvals();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const classStudents = getClassStudents();

  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="quranic-daily-eval-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">مراقبة يومية</h1>
          <p className="text-muted-foreground text-sm">Évaluation Quotidienne Coranique - Vue hebdomadaire</p>
        </div>
        <Button 
          className="rounded-full" 
          onClick={handleSaveAll}
          disabled={saving || !selectedClass}
          data-testid="save-all-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer tout'}
        </Button>
      </div>

      {/* Sélection classe et navigation semaine */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>الفصل (Classe)</Label>
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger data-testid="select-class">
                  <SelectValue placeholder="Sélectionner une classe" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>الأسبوع (Semaine)</Label>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setWeekOffset(prev => prev - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1 text-center font-medium px-4 py-2 border rounded-md bg-muted">
                  S{weekNumber} - {weekYear}
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setWeekOffset(prev => prev + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info classe */}
      {selectedClass && (
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <Badge variant="outline" className="gap-1">
            <Users className="w-3 h-3" />
            {classStudents.length} élèves
          </Badge>
          <Badge variant="secondary" className="gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(weekDates[0]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(weekDates[4]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Badge>
          {getTeacherForClass() && (
            <Badge variant="outline" className="gap-1">
              Prof: {getTeacherForClass()}
            </Badge>
          )}
        </div>
      )}

      {/* Légende */}
      <div className="flex flex-wrap gap-3 text-sm">
        {EVAL_STATUSES.map(status => (
          <div key={status.value} className="flex items-center gap-1">
            <span className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold ${status.color.split(' ')[0]}`}>
              {status.label}
            </span>
            <span className="text-muted-foreground">{status.title}</span>
          </div>
        ))}
      </div>

      {/* Tableau d'évaluation par type */}
      {selectedClass ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BookOpen className="w-5 h-5" />
              {getClassName()} - S{weekNumber}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3 mb-4">
                {EVAL_TYPES.map(type => (
                  <TabsTrigger key={type.value} value={type.value} className="gap-2">
                    {type.label} ({type.label_fr})
                  </TabsTrigger>
                ))}
              </TabsList>

              {EVAL_TYPES.map(evalType => (
                <TabsContent key={evalType.value} value={evalType.value}>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead className="w-[50px] text-center">الرقم</TableHead>
                          <TableHead className="min-w-[150px]">الاسم</TableHead>
                          {evalType.value === 'bind' && (
                              <>
                              <TableHead className="text-center min-w-[120px]">
                                <div className="flex flex-col items-center">
                                  <span className="font-bold">السورة</span>
                                  <span className="text-xs text-muted-foreground">Sourate</span>
                                </div>
                              </TableHead>
                              <TableHead className="text-center min-w-[60px]">
                                <div className="flex flex-col items-center">
                                  <span className="font-bold">من آية</span>
                                  <span className="text-xs text-muted-foreground">Début</span>
                                </div>
                              </TableHead>
                              <TableHead className="text-center min-w-[60px]">
                                <div className="flex flex-col items-center">
                                  <span className="font-bold">إلى آية</span>
                                  <span className="text-xs text-muted-foreground">Fin</span>
                                </div>
                              </TableHead>
                            </>
                          )}
                          {WEEKDAYS.map((day, idx) => (
                            <TableHead key={day.id} className="text-center min-w-[80px]">
                              <div className="flex flex-col items-center">
                                <span className="font-bold">{day.name_ar}</span>
                                <span className="text-xs text-muted-foreground">{day.name_fr}</span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(weekDates[idx]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                </span>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classStudents.map((student, idx) => {
                          const progress = studentProgress[student.id];
                          const lastSurah = progress ? SURAHS.find(s => s.id === progress.lastSurah) : null;
                          
                          return (
                          <TableRow key={student.id}>
                            <TableCell className="font-medium text-center">{idx + 1}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{student.first_name} {student.last_name}</p>
                                <p className="text-xs text-muted-foreground">{student.matricule}</p>
                                {evalType.value === 'bind' && progress && lastSurah && (
                                  <div className="mt-1 px-2 py-1 bg-blue-50 rounded text-xs">
                                    <span className="text-blue-600 font-medium">📖 </span>
                                    <span className="text-blue-800">{lastSurah.name} v.{progress.lastEndVerse}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            {evalType.value === 'bind' && (
                              <>
                                <TableCell className="text-center p-1">
                                  <Select
                                    value={studentSurahData[student.id]?.surah || ''}
                                    onValueChange={(v) => updateStudentSurah(student.id, 'surah', v)}
                                  >
                                    <SelectTrigger className="w-40">
                                      <SelectValue placeholder="Sourate..." />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-60">
                                      {SURAHS.map(s => (
                                        <SelectItem key={s.id} value={String(s.id)}>
                                          <span className="flex items-center gap-2">
                                            <span className="font-bold">{s.id}.</span>
                                            <span>{s.name}</span>
                                            <span className="text-muted-foreground text-xs">({s.verses})</span>
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {studentSurahData[student.id]?.surah && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.name_fr}
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="text-center p-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    max={SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.verses || 286}
                                    placeholder="1"
                                    value={studentSurahData[student.id]?.startVerse || ''}
                                    onChange={(e) => updateStudentSurah(student.id, 'startVerse', e.target.value)}
                                    className="w-16 p-1 text-sm text-center"
                                  />
                                </TableCell>
                                <TableCell className="text-center p-1">
                                  <Input
                                    type="number"
                                    min="1"
                                    max={SURAHS.find(s => s.id === parseInt(studentSurahData[student.id]?.surah))?.verses || 286}
                                    placeholder="10"
                                    value={studentSurahData[student.id]?.endVerse || ''}
                                    onChange={(e) => updateStudentSurah(student.id, 'endVerse', e.target.value)}
                                    className="w-16 p-1 text-sm text-center"
                                  />
                                </TableCell>
                              </>
                            )}
                            {WEEKDAYS.map((day, dayIdx) => {
                              const evalData = getEvaluation(student.id, evalType.value, weekDates[dayIdx]);
                              return (
                                <TableCell key={day.id} className="text-center p-1">
                                  <div className="flex flex-wrap justify-center gap-1">
                                    {EVAL_STATUSES.map(status => (
                                      <Button
                                        key={status.value}
                                        size="sm"
                                        variant={evalData.status === status.value ? "default" : "outline"}
                                        className={`w-7 h-7 p-0 text-xs font-bold ${evalData.status === status.value ? status.color : ''}`}
                                        onClick={() => updateEvaluation(student.id, evalType.value, weekDates[dayIdx], status.value)}
                                        title={status.title}
                                      >
                                        {status.label}
                                      </Button>
                                    ))}
                                  </div>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg">Sélectionnez une classe</h3>
            <p className="text-muted-foreground">Choisissez une classe pour commencer la saisie des évaluations</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
